import isNil from 'lodash/isNil';
import toUpper from 'lodash/toUpper';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { UnitMeasurementSystem } from '@/constants/enums';
import {
  CompressorPriority,
  EngineStatusName,
  numericFields,
  RejectionCause,
  RejectionCauseLabels,
  RejectionReason,
  RejectReasonLabels,
} from './constants';
import { MetricSetpoint, roundOff, Setpoint } from './common';
import { DeviceMetadata, EditableField } from '@/store/Devices/constants';
import { EngineTab } from '@/constants';
import { ValidationMessage } from '@/types';
import { MainRangesConfiguration } from '@/store/Engine/MainRangesConfiguration';
import { Scenario } from './FallbackScenarios';
import { token } from '@/theme';

export const converterFactory = (from, to) => (key, v) =>
  key === 'upperBound' && v === from ? to : numericFields.includes(key) ? Number.parseFloat(v) || v : v;

export const convertInfinityToNull = converterFactory(Infinity, null);
export const convertNullToInfinity = converterFactory(null, Infinity);

export const convertToUnitMeasurementFactory = (unit, to) => (value, tvalue) => {
  if (value === '') return null;
  else if (isNil(value) || value === Infinity) return value;
  return `${tvalue(
    value,
    unit,
    toUpper(to),
    toUpper(to === UnitMeasurementSystem.Metric ? UnitMeasurementSystem.Imperial : UnitMeasurementSystem.Metric),
  )}`;
};
export const convertFlowMinToMetric = convertToUnitMeasurementFactory('cfm', UnitMeasurementSystem.Metric);
export const convertFlowMinToImperial = convertToUnitMeasurementFactory('m^3/min', UnitMeasurementSystem.Imperial);
export const convertPressureToMetric = convertToUnitMeasurementFactory('psi', UnitMeasurementSystem.Metric);
export const convertPressureToImperial = convertToUnitMeasurementFactory('bar', UnitMeasurementSystem.Imperial);

export const isEditable = (field: string, mappedEditableFields: string | string[]) => {
  return mappedEditableFields.includes(field);
};

export const isEmptyField = value => {
  if (typeof value === 'number') {
    return false;
  }
  return isEmpty(value);
};

export function getUIRejectReasonLabel(reason: RejectionReason): string {
  return RejectReasonLabels[reason];
}

export function getUIRejectionCauseLabel(cause: RejectionCause): string {
  return RejectionCauseLabels[cause];
}

export const sortSetpointsByElementIndex = (setpoints: Setpoint[], sortedCompressors: DeviceMetadata[]) => {
  return setpoints?.slice()?.sort((a, b) => {
    const aMetadata = find(sortedCompressors, { id: a.compressorId });
    const bMetadata = find(sortedCompressors, { id: b.compressorId });

    if (aMetadata?.elementIndex !== undefined && bMetadata?.elementIndex !== undefined) {
      return aMetadata.elementIndex - bMetadata.elementIndex;
    }
    return 0;
  });
};
/**
 * Creates bandsetpoints object from device metrics
 * @param setpoint The setpoint object containing compressorId
 * @param engineMetrics Array of metric setpoints
 * @returns Bandsetpoints object or null if no metrics found
 */
export const createBandsetpoints = (setpoint: Setpoint, engineMetrics: MetricSetpoint[] = []) => {
  const deviceMetrics = engineMetrics
    .filter(metric => metric.deviceId === setpoint.compressorId)
    .reduce((acc, metric) => {
      acc[metric.metricType] = metric.value;
      return acc;
    }, {} as Record<string, number>);

  const bandsetpoints =
    Object.keys(deviceMetrics).length > 0
      ? {
          minimumPressureBand1: deviceMetrics?.minimumPressureBand1Bar,
          maximumPressureBand1: deviceMetrics?.maximumPressureBand1Bar,
          minimumPressureBand2: deviceMetrics?.minimumPressureBand2Bar,
          maximumPressureBand2: deviceMetrics?.maximumPressureBand2Bar,
        }
      : null;

  return bandsetpoints;
};

/**
 * Validates setpoints against compressor thresholds
 * @param setpoints Array of setpoints to validate
 * @param devicesMetadata
 * @param mainRangesConfiguration
 * @returns Array of validation messages
 */
export const validateSetpoints = (
  setpoints: Setpoint[],
  devicesMetadata?: DeviceMetadata[],
  mainRangesConfiguration?: MainRangesConfiguration,
): ValidationMessage[] => {
  const validationMessages: ValidationMessage[] = [];

  let filteredSetpoints = setpoints;
  if (devicesMetadata) {
    const ignoredCompressorIds = new Set(
      devicesMetadata
        .filter(compressor => {
          const priority = compressor?.attributes?.priority;
          return priority === CompressorPriority.Ignored;
        })
        .map(c => c.id),
    );
    filteredSetpoints = setpoints?.filter(setpoint => !ignoredCompressorIds.has(setpoint.compressorId)) || [];
  }

  const shouldValidateOnlyMiddleTables =
    mainRangesConfiguration?.flowLevels?.ranges?.length && mainRangesConfiguration?.pressureLevels?.ranges?.length > 2;

  const middleTables: string[] = [];
  if (shouldValidateOnlyMiddleTables) {
    mainRangesConfiguration.flowLevels.ranges.forEach(flowRange => {
      const firstPressureRanges = mainRangesConfiguration.pressureLevels.ranges
        .filter(r => r.parentRangeKey === flowRange.key)
        .map(r => r.key);
      const tables = Array.from(
        new Set(
          filteredSetpoints
            .filter(s => firstPressureRanges.includes(s.parentRangeKey))
            .map(s => s.name)
            .filter(name => name != null),
        ),
      ).slice(1, -1);
      middleTables.push(...tables);
    });
  }

  filteredSetpoints?.forEach(setpoint => {
    let editableFields: EditableField[] = null;
    if (devicesMetadata) {
      const deviceMetadata = find(devicesMetadata, { id: setpoint.compressorId });
      editableFields = deviceMetadata?.editableFields;
    }

    const otherSetpointsInRange = filteredSetpoints.filter(
      s => s.parentRangeKey === setpoint.parentRangeKey && s.name && middleTables.includes(s.name),
    );

    if (
      (!editableFields || editableFields?.includes(EditableField.ORDER)) &&
      (!middleTables?.length ||
        (setpoint.name && middleTables.includes(setpoint.name)) ||
        (!setpoint.name && setpoint.parentRangeKey && otherSetpointsInRange.length > 0)) &&
      (setpoint.order === null ||
        setpoint.order === undefined ||
        (typeof setpoint.order === 'string' && setpoint.order === ''))
    ) {
      validationMessages.push({
        key: setpoint?.key,
        field: 'order',
        message: 'orderIsRequired',
        focused: false,
        tab: setpoint?.parentRangeKey ? EngineTab.DefaultSetpoints : EngineTab.DefaultSetpointsConfiguration,
      });
    }
    if (
      (!editableFields || editableFields?.includes(EditableField.PRESSURE_DROP)) &&
      (setpoint.pressureDrop === null || setpoint.pressureDrop === undefined)
    ) {
      validationMessages.push({
        key: setpoint?.key,
        field: 'pressureDrop',
        message: 'pressureDropIsRequired',
        focused: false,
        tab: setpoint?.parentRangeKey ? EngineTab.DefaultSetpoints : EngineTab.DefaultSetpointsConfiguration,
      });
    }
  });
  // g// group setpoints by setpoints.name. In each group, check that there is at least one setpoint with order > 0.
  const setpointGroups =
    filteredSetpoints?.reduce((groups, setpoint) => {
      if (!middleTables.length || middleTables.includes(setpoint.name)) {
        const groupName = setpoint.name;
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(setpoint);
      }
      return groups;
    }, {} as Record<string, Setpoint[]>) ?? {};

  Object.values(setpointGroups).forEach(groupSetpoints => {
    const hasValidOrder = groupSetpoints.some(
      setpoint => setpoint.order !== null && setpoint.order !== undefined && Number(setpoint.order) > 0,
    );
    if (!hasValidOrder) {
      const invalidSetpoint = groupSetpoints.find(
        setpoint => setpoint.order !== null && setpoint.order !== undefined && Number(setpoint.order) <= 0,
      );

      if (invalidSetpoint) {
        validationMessages.push({
          key: invalidSetpoint?.key,
          field: 'order',
          message: 'atLeastOneSetpointMustHavePositiveOrder',
          focused: false,
          tab: invalidSetpoint?.parentRangeKey ? EngineTab.DefaultSetpoints : EngineTab.DefaultSetpointsConfiguration,
        });
      }
    }
  });

  return validationMessages;
};

/**
 * Converts setpoint values based on unit measurement system
 * @param setpoint The setpoint to convert
 * @param mappedEditableFields Array of editable fields
 * @param tvalue Temperature value for conversion
 * @param unitMeasurementSystem Current unit measurement system
 * @param bandsetpoints Optional bandsetpoints to convert
 * @returns Converted setpoint object
 */
export const convertSetpointValues = (
  setpoint,
  mappedEditableFields,
  tvalue,
  unitMeasurementSystem,
  bandsetpoints = null,
) => {
  if (unitMeasurementSystem === UnitMeasurementSystem.Imperial) {
    const baseSetpoint = {
      ...setpoint,
      load: isEditable('load', mappedEditableFields) ? roundOff(convertPressureToMetric(setpoint?.load, tvalue)) : null,
      unload: isEditable('unload', mappedEditableFields)
        ? roundOff(convertPressureToMetric(setpoint?.unload, tvalue))
        : null,
      pressureDrop: isEditable('pressureDrop', mappedEditableFields)
        ? roundOff(convertPressureToMetric(setpoint?.pressureDrop, tvalue))
        : null,
      autoStop: isEditable('auto', mappedEditableFields)
        ? roundOff(convertPressureToMetric(setpoint?.autoStop, tvalue))
        : null,
      immediateStop: isEditable('immediate', mappedEditableFields)
        ? roundOff(convertPressureToMetric(setpoint?.immediateStop, tvalue))
        : null,
      vsdTarget: isEditable('target', mappedEditableFields)
        ? roundOff(convertPressureToMetric(setpoint?.vsdTarget, tvalue))
        : null,
      modulate: isEditable('modulation', mappedEditableFields) ? setpoint?.modulate : null,
      bandConfiguration: isEditable('band', mappedEditableFields) ? setpoint?.bandConfiguration : null,
    };

    if (bandsetpoints) {
      return {
        ...baseSetpoint,
        bandsetpoints: {
          minimumPressureBand1: roundOff(
            parseFloat(convertPressureToMetric(bandsetpoints.minimumPressureBand1, tvalue)),
          ),
          maximumPressureBand1: roundOff(
            parseFloat(convertPressureToMetric(bandsetpoints.maximumPressureBand1, tvalue)),
          ),
          minimumPressureBand2: roundOff(
            parseFloat(convertPressureToMetric(bandsetpoints.minimumPressureBand2, tvalue)),
          ),
          maximumPressureBand2: roundOff(
            parseFloat(convertPressureToMetric(bandsetpoints.maximumPressureBand2, tvalue)),
          ),
        },
      };
    }

    return baseSetpoint;
  } else {
    const baseSetpoint = {
      ...setpoint,
      load: roundOff(setpoint?.load),
      unload: roundOff(setpoint?.unload),
      autoStop: roundOff(setpoint?.autoStop),
      immediateStop: roundOff(setpoint?.immediateStop),
      vsdTarget: roundOff(setpoint?.vsdTarget),
      pressureDrop: roundOff(setpoint?.pressureDrop),
    };

    if (bandsetpoints) {
      return {
        ...baseSetpoint,
        bandsetpoints: {
          minimumPressureBand1: roundOff(bandsetpoints.minimumPressureBand1),
          maximumPressureBand1: roundOff(bandsetpoints.maximumPressureBand1),
          minimumPressureBand2: roundOff(bandsetpoints.minimumPressureBand2),
          maximumPressureBand2: roundOff(bandsetpoints.maximumPressureBand2),
        },
      };
    }

    return baseSetpoint;
  }
};

export const convertFallbackScenariosToUnitMeasurementSystemForNewFallbackScenarios = (
  fallbackScenarios: Scenario[],
  tvalue,
) => {
  return fallbackScenarios?.map(scenario => ({
    ...scenario,
    fallbackRules: scenario?.fallbackRules?.map(fallbackRule => ({
      ...fallbackRule,
      rules: fallbackRule?.rules?.map(rule => ({
        ...rule,
        load: convertPressureToImperial(rule?.load, tvalue),
        unload: convertPressureToImperial(rule?.unload, tvalue),
        autoStop: convertPressureToImperial(rule?.autoStop, tvalue),
        immediateStop: convertPressureToImperial(rule?.immediateStop, tvalue),
        vsdTarget: convertPressureToImperial(rule?.vsdTarget, tvalue),
        pressureDrop: convertPressureToImperial(rule?.pressureDrop, tvalue),
      })),
    })),
  }));
};

/**
 * Validates setpoints for new fallback scenarios - only checks editable setpoints
 * @param rules Array of setpoint rules to validate
 * @param disabledCompressors Array of disabled compressor IDs
 * @returns Array of validation messages
 */
export const validateNewFallbackScenarioSetpoints = (
  rules,
  disabledCompressors,
  devicesMetadata?: DeviceMetadata[],
) => {
  const validationMessages = [];
  const editableSetpoints = rules?.filter(setpoint => !disabledCompressors?.includes(setpoint.compressorId)) || [];

  editableSetpoints.forEach(setpoint => {
    let editableFields: EditableField[] = null;
    if (devicesMetadata) {
      const deviceMetadata = find(devicesMetadata, { id: setpoint.compressorId });
      editableFields = deviceMetadata?.editableFields;
    }
    const orderValue = setpoint.order;
    if (
      (!editableFields || editableFields?.includes(EditableField.ORDER)) &&
      (orderValue === null || orderValue === undefined || (typeof orderValue === 'string' && orderValue === ''))
    ) {
      validationMessages.push({
        key: setpoint.key,
        field: 'order',
        message: 'orderIsRequired',
        focused: false,
      });
    }
    if (
      (!editableFields || editableFields?.includes(EditableField.PRESSURE_DROP)) &&
      (setpoint.pressureDrop === null || setpoint.pressureDrop === undefined)
    ) {
      validationMessages.push({
        key: setpoint?.key,
        field: 'pressureDrop',
        message: 'pressureDropIsRequired',
        focused: false,
        tab: setpoint?.parentRangeKey ? EngineTab.DefaultSetpoints : EngineTab.DefaultSetpointsConfiguration,
      });
    }
  });

  const hasPositiveOrder = editableSetpoints.some(
    setpoint => setpoint.order !== null && setpoint.order !== undefined && Number(setpoint.order) > 0,
  );

  if (!hasPositiveOrder && editableSetpoints.length > 0) {
    const invalidSetpoint = editableSetpoints.find(
      setpoint => setpoint.order !== null && setpoint.order !== undefined && Number(setpoint.order) <= 0,
    );
    if (invalidSetpoint) {
      validationMessages.push({
        key: invalidSetpoint.key,
        field: 'order',
        message: 'atLeastOneSetpointMustHavePositiveOrder',
        focused: false,
      });
    }
  }

  return validationMessages;
};

const ENGINE_STATUS_VALUES = Object.values(EngineStatusName) as string[];

export const isValidEngineStatusName = (value: unknown): value is EngineStatusName => {
  return typeof value === 'string' && ENGINE_STATUS_VALUES.includes(value);
};

export const getEngineStatusColor = (status: EngineStatusName | null): string => {
  switch (status) {
    case EngineStatusName.Active:
    case EngineStatusName.ActiveFallback:
      return token.colorSuccess;
    case EngineStatusName.Maintenance:
    case EngineStatusName.Disabled:
      return token.colorWarningText;
    case EngineStatusName.Inactive:
      return token.colorErrorText;
    case EngineStatusName.Unavailable:
      return token.colorBorder;
    default:
      return token.colorError;
  }
};
